import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { changeDataIsoStringToDate } from '../../utils/parse.utils';
import { PlatformService } from '../platform/platform.service';

export enum LocalDataKey {
  AnalyticsUserData = 'analyticsUserData',
  AnonymousUserId = 'anonymousUserId',
  Apportions = 'apportions',
  CookiesAcceptance = 'cookiesAcceptance',
  Favorites = 'favorites',
  ForcePopupSignIn = 'forcePopupSignIn',
  ExperimentMortgageSimulatorContactDataReminderType = 'experimentMortgageSimulatorContactDataReminderType',
  ExperimentSignInMessageType = 'experimentSignInMessageType',
  HideBirdOffer = 'hideBirdOffer',
  HideTestingEnvironmentAlert = 'hideTestingEnvironmentAlert',
  LocalUser = 'localUser',
  MockAccounts = 'mockAccounts',
  MockAddresses = 'mockAddresses',
  MockChargeIntents = 'mockChargeIntents',
  MockEcommerceOrders = 'mockEcommerceOrders',
  MockFavorites = 'mockFavorites',
  MockIdToken = 'mockIdToken',
  MockIdTokenResult = 'mockIdTokenResult',
  MockMovingOrders = 'mockMovingOrders',
  MockPaymentIntents = 'mockPaymentIntents',
  MockRemodelingFinancingSimulations = 'mockRemodelingFinancingSimulations',
  MockRemodelingOrders = 'mockRemodelingOrders',
  MockShoppingCarts = 'mockShoppingCarts',
  MockUserId = 'mockUserId',
  MockUsers = 'mockUsers',
  MockWarehouseOrders = 'mockWarehouseOrders',
  MortgageSimulation = 'mortgageSimulation',
  PostalCode = 'postalCode',
  PendingRemodelingQuote = 'pendingRemodelingQuote',
  ProjectSimulation = 'projectSimulation',
  SearchHistory = 'searchHistory',
  ShoppingCart = 'shoppingCart',
  TermsOfServiceAcceptance = 'termsOfServiceAcceptance',
  TrackedProducts = 'trackedProducts',
  TrackingParams = 'trackingParams',
}

const ignoreKeys = [
  'cache',
];

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  private data: Record<string, BehaviorSubject<any>> = {};

  constructor(
    private platformService: PlatformService,
  ) {
    if (this.platformService.isBrowser()) {
      Object.keys(localStorage).forEach((key) => {
        if (ignoreKeys.some((ignoreKey) => key.startsWith(ignoreKey))) {
          return;
        }
        const value = localStorage.getItem(key) as string;
        try {
          this.data[key] = new BehaviorSubject(changeDataIsoStringToDate(JSON.parse(value)));
          // eslint-disable-next-line no-empty
        } catch (error) {}
      });
    }
  }

  clear(key: LocalDataKey | string): void {
    if (this.data[key]) {
      this.data[key].next(null);
    }
    if (this.platformService.isBrowser()) {
      localStorage.removeItem(key);
    }
  }

  clearAll(): void {
    if (this.platformService.isBrowser()) {
      if (environment.usingMocks) {
        Object.keys(localStorage).forEach((key) => {
          if (!key.startsWith('mock')) {
            localStorage.removeItem(key);
            this.data[key].next(null);
          }
        });
      } else {
        localStorage.clear();
        Object.keys(this.data).forEach((key) => this.data[key].next(null));
      }
    }
  }

  get<T>(key: LocalDataKey): Observable<T | null> {
    if (!this.data[key]) {
      this.data[key] = new BehaviorSubject<T | null>(null);
    }
    return this.data[key] as Observable<T>;
  }

  getItem<T>(key: LocalDataKey | string): T | null {
    if (this.platformService.isBrowser()) {
      const rawData = localStorage.getItem(key);
      if (rawData) {
        return changeDataIsoStringToDate(JSON.parse(rawData) as T);
      }
    }
    return null;
  }

  getKeys(): string[] {
    return Object.keys(localStorage);
  }

  set<T>(key: LocalDataKey | string, data: T): void {
    if (this.platformService.isBrowser()) {
      if (typeof data === 'boolean' || data) {
        localStorage.setItem(key, JSON.stringify(data));
      }
      if (!this.data[key]) {
        this.data[key] = new BehaviorSubject<T>(data);
      }
      this.data[key].next(data);
    }
  }
}

